import type { ObjectToCamel } from "ts-case-convert";
import type { Ref } from "vue";

import { objectToSnake } from "ts-case-convert";
import { computed, toValue } from "vue";

import { useI18nMainSiteCompanyWall } from "@solvari/translations";

import { useZiggy } from "@/shared/libs/ziggy";
import { buildLocalSearchCompanySlug } from "~/features/company-wall/companyWallUrl.ts";
import { useHost } from "~/shared/composables/useHost.ts";
import {
  useSeoNormalized,
  useStructuredData,
} from "~/shared/composables/useMetaData.ts";

import {
  metaDescriptionFallback,
  metaTitleFallback,
} from "./useLocalSearchTranslations.ts";

function useCompanyWallDetailsSeo(params: {
  customerName: Ref<string>;
  data: Ref<ObjectToCamel<App.Data.CompanyWallData> | undefined>;
  routeSlug: string;
}) {
  const { tr } = useI18nMainSiteCompanyWall();
  const host = useHost();
  const headZiggy = useZiggy();

  const canonical = computed(() => {
    const data = toValue(params.data);

    if (
      !data?.canonicalGeoProductCategorySlug ||
      !data.provinceSlug ||
      !data.municipalitySlug
    ) {
      const slug = data?.companyCard.slug ?? params.routeSlug;
      return `https://${host}${headZiggy("mainSite.companyWall.companyWallDetails", { slug })}`;
    }

    return `https://${host}${headZiggy(
      "mainSite.localSearch.companyWallDetails",
      {
        geoProductSlug: data.canonicalGeoProductCategorySlug,
        province: data.provinceSlug,
        municipality: data.municipalitySlug,
        slug: buildLocalSearchCompanySlug(
          data.companyCard.slug,
          data.subMunicipalitySlug,
          data.municipalitySlug,
        ),
      },
    )}`;
  });

  const metaTitle = computed(() => {
    const data = toValue(params.data);
    const municipality = data?.locations[0]?.city;

    if (!municipality) {
      return tr(
        "main_site.company_wall.details.seo_title_fallback_no_city",
        objectToSnake({ customerName: params.customerName.value }),
      );
    }

    const translations = [
      "main_site.company_wall.details.seo_title_1",
      "main_site.company_wall.details.seo_title_2",
      "main_site.company_wall.details.seo_title_3",
      "main_site.company_wall.details.seo_title_4",
      "main_site.company_wall.details.seo_title_5",
      "main_site.company_wall.details.seo_title_6",
      "main_site.company_wall.details.seo_title_7",
    ] as const;

    return metaTitleFallback(
      translations.map((key) =>
        tr(
          key,
          objectToSnake({
            customerName: params.customerName.value,
            municipality,
          }),
        ),
      ),
    );
  });

  const metaDescription = computed(() => {
    const data = toValue(params.data);
    const product = data?.geoProductCategories[0]?.name;
    const municipality = data?.locations[0]?.city;

    if (product && municipality) {
      const translations = [
        "main_site.company_wall.details.seo_description_1",
        "main_site.company_wall.details.seo_description_2",
        "main_site.company_wall.details.seo_description_3",
        "main_site.company_wall.details.seo_description_4",
        "main_site.company_wall.details.seo_description_5",
      ] as const;

      return metaDescriptionFallback(
        translations.map((key) =>
          tr(
            key,
            objectToSnake({
              product,
              municipality,
              customerName: params.customerName.value,
            }),
          ),
        ),
      );
    }

    if (product) {
      return tr(
        "main_site.company_wall.details.seo_description_fallback_no_city",
        objectToSnake({ customerName: params.customerName.value, product }),
      );
    }

    if (municipality) {
      return tr(
        "main_site.company_wall.details.seo_description_fallback_no_product",
        objectToSnake({
          customerName: params.customerName.value,
          municipality,
        }),
      );
    }

    return tr(
      "main_site.company_wall.details.seo_description_fallback_no_city_and_product",
      objectToSnake({ customerName: params.customerName.value }),
    );
  });

  useSeoNormalized({
    title: metaTitle,
    description: metaDescription,
    canonical,
    ogImage: () => toValue(params.data)?.companyCard.logo,
    articleModifiedTime: () => toValue(params.data)?.updatedAt,
    // Without a logo the base fallback image applies, which is not 250×250.
    ogImageWidth: () =>
      toValue(params.data)?.companyCard.logo ? 250 : undefined,
    ogImageHeight: () =>
      toValue(params.data)?.companyCard.logo ? 250 : undefined,
  });

  /* eslint-disable @typescript-eslint/naming-convention */
  useStructuredData(() => {
    const data = toValue(params.data);
    if (!data) {
      return;
    }
    return {
      "@type": "Corporation",
      "@id": canonical.value,
      name: params.customerName.value,
      url: canonical.value,
      logo: data.companyCard.logo,
      description: data.descriptionLong ?? data.companyCard.description,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical.value },
      aggregateRating: data.companyCard.reviews
        ? {
            "@type": "AggregateRating",
            ratingValue: data.companyCard.reviews.average,
            reviewCount: data.companyCard.reviews.total,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
      hasOfferCatalog: data.geoProductCategories.length
        ? {
            "@type": "OfferCatalog",
            name: "Services",
            itemListElement: data.geoProductCategories.map(
              (geoProductCategory) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: geoProductCategory.name,
                },
              }),
            ),
          }
        : undefined,
      location: data.locations.length
        ? data.locations.map((address) => ({
            "@type": "PostalAddress",
            addressLocality: address.city,
            addressCountry: address.countryCode,
            postalCode: address.zipCode,
            streetAddress: `${address.street} ${address.houseNo}`,
          }))
        : undefined,
    };
  });
  /* eslint-enable @typescript-eslint/naming-convention */

  return { canonical };
}

export { useCompanyWallDetailsSeo };
